'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useHoverIntent } from 'react-use-hoverintent'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGrid } from '@awesome.me/kit-94e593bf23/icons/sharp/solid'
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom'

/**
 * FeaturedProductsPopup - Reusable component for displaying featured products
 *
 * Shows a grid icon that, when hovered or clicked, reveals a popup menu
 * with links to featured products.
 *
 * @param {Array} products - Array of product objects with name, url, price, image
 * @param {string} position - Position of the component: 'bottom-left', 'bottom-right', 'top-left', 'top-right'
 * @param {boolean} imageLeft - If true, show image on left side of each row
 */
const FeaturedProductsPopup = ({ products, position = 'bottom-right', imageLeft = false }) => {
    const [featureOpen, setFeatureOpen] = useState(false)
    const featureContainerRef = useRef(null)
    const closeTimeoutRef = useRef(null)
    const isHoveringRef = useRef(false)

    // Hover intent for opening
    const [isHoverIntent, hoverIntentRef, setIsHoverIntent] = useHoverIntent({
        sensitivity: 6,
        interval: 100,
        timeout: 200
    })

    // Sync hover intent with featureOpen
    useEffect(() => {
        if (isHoverIntent) {
            setFeatureOpen(true)
        }
    }, [isHoverIntent])

    // Position classes for the container (aligned with --site-padding CSS variable)
    const positionClasses = {
        'bottom-left': 'bottom-6 left-[var(--site-padding)]',
        'bottom-right': 'bottom-6 right-[var(--site-padding)]',
        'top-left': 'top-3 left-[var(--site-padding)]',
        'top-right': 'top-3 right-[var(--site-padding)]',
    }

    // Popup position classes - always above button
    const popupPositionClasses = {
        'bottom-left': 'bottom-10 left-0',
        'bottom-right': 'bottom-10 right-0',
        'top-left': 'top-10 left-0',
        'top-right': 'top-10 right-0',
    }

    // Popup hidden state transform based on position
    const popupHiddenClasses = {
        'bottom-left': 'translate-y-2',
        'bottom-right': 'translate-y-2',
        'top-left': '-translate-y-2',
        'top-right': '-translate-y-2',
    }

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (featureContainerRef.current && !featureContainerRef.current.contains(event.target)) {
                setFeatureOpen(false)
            }
        }

        if (featureOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [featureOpen])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current)
            }
        }
    }, [])

    const handleClick = useCallback(() => {
        setFeatureOpen(prev => !prev)
    }, [])

    // Clear timeout and mark as hovering when mouse enters popup
    const handleMenuEnter = useCallback(() => {
        isHoveringRef.current = true
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
        }
    }, [])

    // Start close timer only when mouse leaves the popup
    const handleMenuLeave = useCallback(() => {
        isHoveringRef.current = false
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
        }
        closeTimeoutRef.current = setTimeout(() => {
            if ( ! isHoveringRef.current) {
                setFeatureOpen(false)
            }
        }, 1000)
    }, [])

    const validProducts = products?.filter(product => product.url) || []

    if (validProducts.length === 0) {
        return null
    }

    return (
        <div ref={featureContainerRef} className={`absolute ${positionClasses[position]} z-30`}>
            <div
                role="menu"
                aria-label="Featured Products"
                onMouseEnter={handleMenuEnter}
                onMouseLeave={handleMenuLeave}
                className={`absolute ${popupPositionClasses[position]} bg-white rounded-sm shadow-2xl border border-black/[.1] overflow-hidden transition-all duration-300 ease-in-out ${
                    featureOpen
                        ? 'opacity-100 pointer-events-auto'
                        : `opacity-0 ${popupHiddenClasses[position]} pointer-events-none`
                }`}
            >
                <div className="w-64 relative">
                    <div className="divide-y divide-gray-8">
                        {validProducts.map((product, index) => (
                            <Link
                                key={index}
                                href={product.url}
                                role="menuitem"
                                className={`flex items-center justify-between gap-3 px-4 py-3 ${imageLeft ? 'flex-row-reverse' : ''}`}
                            >
                                <div className={`${imageLeft ? 'text-left' : 'text-right'} flex-1`}>
                                    <div className="text-sm text-dark capitalize">{product.name}</div>
                                    {product.price && (
                                        <div className="text-3xs text-light">
                                            <div>{product.price}</div>
                                            {product.originalPrice && (
                                                <div className="line-through">{product.originalPrice}</div>
                                            )}
                                        </div>
                                    )}
                                    {product.onSale && product.discountPercentage > 0 && (
                                        <div className="text-2xs text-red">
                                            <span className="font-semibold">Sale</span> {product.discountPercentage}% off
                                        </div>
                                    )}
                                </div>
                                {product.image?.file_name && (
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${product.image.file_name}`}
                                        alt={product.name || 'Product image'}
                                        width={38}
                                        height={38}
                                        className="w-[38px] h-[38px] object-contain flex-shrink-0"
                                    />
                                )}
                            </Link>
                        ))}
                    </div>
                    <FontAwesomeIcon icon={faHiveIconSmallLight} className="absolute bottom-2 left-3 text-primary text-lg" />
                </div>
            </div>
            <button
                ref={hoverIntentRef}
                onClick={handleClick}
                onMouseEnter={handleMenuEnter}
                onMouseLeave={handleMenuLeave}
                aria-label="View featured products"
                aria-expanded={featureOpen}
                aria-haspopup="true"
                className={`p-1 aspect-square w-7 h-7 text-center flex flex-col justify-center items-center group transition-all duration-300 shadow-lg hover:shadow-xl ${featureOpen ? 'bg-primary' : 'bg-white/[.15] hover:bg-white/[.3]'}`}
            >
                <FontAwesomeIcon icon={faGrid} className={`text-md transition-all duration-700 group-hover:rotate-90 ${featureOpen ? 'text-white' : 'text-white/[.9] group-hover:text-white'}`} />
            </button>
        </div>
    )
}

export default FeaturedProductsPopup
